/**
 * 3Dセキュア認証コンポーネント（Web版）
 * ZEUS JavaScript SDK (公式ドキュメント準拠) を使用して3DS認証を実行
 * 参考: https://linkpt.cardservice.co.jp/api/token/2.0/zeus_token_cvv2.js
 */

import React, { useEffect, useState, useRef } from "react";
import ReactDOM from "react-dom";
import { getAppConfig } from "../../config";
import "./ThreeDSecureAuth.css";

/**
 * ZEUS JavaScript SDK グローバル関数の型定義
 */
declare global {
    interface Window {
        setPareqParams?: (
            md: string,
            paReq: string,
            termUrl: string,
            threeDSMethod: string,
            iframeUrl: string
        ) => void;
        onPaResSuccess?: (result: any) => void;
        _onPaResSuccess?: (result: any) => void;
        onError?: (error: any) => void;
        _onError?: (error: any) => void;
        zeusTokenIpcode?: string;
    }
}

/**
 * 認証結果の型定義
 */
export interface ThreeDSecureAuthResult {
    success: boolean;
    sessionId?: string;
    transStatus?: string;
    eci?: string;
    authenticationValue?: string;
    errorCode?: string;
    errorMessage?: string;
}

/**
 * コンポーネントProps
 */
export interface ThreeDSecureAuthProps {
    /** enrollで取得したsessionId */
    sessionId: string;
    /** enrollで取得したmd */
    md: string;
    /** enrollで取得したtermUrl */
    termUrl: string;
    /** enrollで取得したiframeUrl */
    iframeUrl: string;
    /** クライアントIPアドレス（ZEUS SDK用） */
    clientIp: string;
    /** 認証完了時のコールバック */
    onComplete: (result: ThreeDSecureAuthResult) => void;
    /** エラー時のコールバック */
    onError: (error: { errorCode?: string; errorMessage?: string }) => void;
}

/**
 * 3Dセキュア認証コンポーネント
 */
export const ThreeDSecureAuth: React.FC<ThreeDSecureAuthProps> = ({
    sessionId,
    md,
    termUrl,
    iframeUrl,
    clientIp,
    onComplete,
    onError
}) => {
    const [isLoading, setIsLoading] = useState(true);
    const containerRef = useRef<HTMLDivElement>(null);
    // 認証が完了したかどうかを示すフラグ（完了後はエラーを表示しない）
    // useRefを使用して、再レンダリング間で値を保持する
    const authCompletedRef = useRef(false);

    useEffect(() => {
        // コンポーネントがアクティブかどうかを示すフラグ
        let isComponentActive = true;

        // ZEUS SDKにクライアントIPを設定（E00002エラー対策）
        // 重要: SDKスクリプトをロードする前に設定する必要がある
        if (clientIp) {
            window.zeusTokenIpcode = clientIp;
        } else {
            console.error(
                "[3DS Auth] clientIp is not provided, ZEUS SDK will fail with E00002"
            );
            authCompletedRef.current = true;
            onError({
                errorCode: "3DS999",
                errorMessage: "クライアントIP情報が取得できませんでした"
            });
            return;
        }

        // ZEUS SDKのグローバルコールバックを設定（onPaResSuccessと_onPaResSuccessの両方）
        const handlePaResSuccess = (result: any) => {
            if (!isComponentActive || authCompletedRef.current) {
                return;
            }

            try {
                result =
                    typeof result === "string" ? JSON.parse(result) : result;
            } catch (e) {
                console.error("[3DS Auth] Failed to parse PaRes result:", e);
                onError({
                    errorCode: "3DS999",
                    errorMessage: "認証結果の解析に失敗しました"
                });
                return;
            }

            // ZEUSから返される結果を処理
            if (result && result.status === "success") {
                authCompletedRef.current = true; // 認証完了フラグを立てる
                onComplete({
                    success: true,
                    sessionId: sessionId,
                    transStatus: result.transStatus,
                    eci: result.eci,
                    authenticationValue: result.authenticationValue
                });
            } else {
                authCompletedRef.current = true; // 失敗も完了として扱う
                onError({
                    errorCode: result?.code || "3DS999",
                    errorMessage: result?.message || "認証に失敗しました"
                });
            }
        };

        const handlePaResError = (error: any) => {
            if (!isComponentActive || authCompletedRef.current) {
                return;
            }

            authCompletedRef.current = true; // エラーも完了として扱う
            onError({
                errorCode: error?.code || "3DS999",
                errorMessage: error?.message || "認証エラーが発生しました"
            });
        };

        // 両方のパターンに対応
        window.onPaResSuccess = handlePaResSuccess;
        window._onPaResSuccess = handlePaResSuccess;
        window.onError = handlePaResError;
        window._onError = handlePaResError;

        // postMessageイベントリスナー登録（termUrlからのコールバック結果を受信）
        const handleMessage = (event: MessageEvent) => {
            if (!isComponentActive || authCompletedRef.current) {
                return;
            }

            // データをパース（文字列の場合はJSON.parse、オブジェクトの場合はそのまま使用）
            let data: any;
            try {
                data =
                    typeof event.data === "string"
                        ? JSON.parse(event.data)
                        : event.data;
            } catch (e) {
                console.error("[3DS Auth] Failed to parse message:", e);
                return;
            }

            // ZEUS SDKからの3DSMethod完了通知（これは無視してOK）
            if (data && data.event === "3DSMethodFinished") {
                return;
            }

            // termUrlからの認証結果通知（statusフィールドで判定）
            if (
                data &&
                (data.status === "success" || data.status === "error")
            ) {
                if (data.status === "success") {
                    authCompletedRef.current = true; // 認証完了フラグを立てる
                    onComplete({
                        success: true,
                        sessionId: sessionId,
                        transStatus: data.transStatus,
                        eci: data.eci,
                        authenticationValue: data.authenticationValue
                    });
                } else {
                    authCompletedRef.current = true; // 失敗も完了として扱う
                    onError({
                        errorCode: data.code,
                        errorMessage: data.message
                    });
                }
                return;
            }
        };

        window.addEventListener("message", handleMessage);

        // ZEUS SDKを動的にロード
        const loadZeusSDK = () => {
            return new Promise<void>((resolve, reject) => {
                // 既にロード済みかチェック
                if (window.setPareqParams) {
                    resolve();
                    return;
                }

                // 既存のscriptタグがあるかチェック
                const existingScript = document.querySelector(
                    'script[src*="zeus_token_cvv2.js"]'
                );
                if (existingScript) {
                    // scriptタグは存在するが、まだロード完了していない可能性
                    const startTime = Date.now();
                    const checkLoaded = () => {
                        if (window.setPareqParams) {
                            resolve();
                        } else if (Date.now() - startTime > 10000) {
                            reject(new Error("ZEUS SDK load timeout"));
                        } else {
                            setTimeout(checkLoaded, 100);
                        }
                    };
                    checkLoaded();
                    return;
                }

                const sdkUrl = getAppConfig().zeus.sdkUrl;
                if (!sdkUrl.startsWith("https://")) {
                    throw new Error("Invalid ZEUS_SDK_URL: must use https");
                }

                const script = document.createElement("script");
                script.type = "text/javascript";
                script.src = sdkUrl;
                script.async = true;

                script.onload = () => {
                    resolve();
                };

                script.onerror = () => {
                    console.error("[3DS Auth] Failed to load ZEUS SDK");
                    reject(new Error("ZEUS SDKの読み込みに失敗しました"));
                };

                document.body.appendChild(script);
            });
        };

        // SDKをロードしてから、setPareqParamsを呼び出す
        const initializeZeus3DS = async () => {
            try {
                // ZEUS SDKをロード
                await loadZeusSDK();

                if (!isComponentActive) {
                    return;
                }

                // ZEUS SDKロード完了後、コールバックを再設定
                // （SDKの初期化処理で上書きされる可能性があるため）
                window.onPaResSuccess = handlePaResSuccess;
                window._onPaResSuccess = handlePaResSuccess;
                window.onError = handlePaResError;
                window._onError = handlePaResError;

                // setPareqParamsが利用可能か確認
                if (!window.setPareqParams) {
                    throw new Error(
                        "setPareqParams is not available after SDK load"
                    );
                }

                // 3dscontainerが存在するか確認
                const container = document.getElementById("3dscontainer");
                if (!container) {
                    throw new Error("3dscontainer element not found in DOM");
                }

                // setPareqParamsを呼び出し
                // paReq: "PaReq"固定、threeDSMethod: "2"固定（ZEUSドキュメント仕様）
                window.setPareqParams(md, "PaReq", termUrl, "2", iframeUrl);

                setIsLoading(false);
            } catch (error) {
                console.error("[3DS Auth] Initialization error:", error);
                if (isComponentActive && !authCompletedRef.current) {
                    authCompletedRef.current = true;
                    onError({
                        errorCode: "3DS999",
                        errorMessage: "3DS認証の初期化に失敗しました"
                    });
                }
            }
        };

        initializeZeus3DS();

        return () => {
            // コンポーネントが非アクティブになったことを示す
            isComponentActive = false;

            window.removeEventListener("message", handleMessage);

            // コールバック関数を元のグローバルガードに戻す
            window.onPaResSuccess = (result: any) => {
                console.warn(
                    "[Global ZEUS SDK] onPaResSuccess called but no active 3DS session:",
                    result
                );
            };
            window._onPaResSuccess = (result: any) => {
                console.warn(
                    "[Global ZEUS SDK] _onPaResSuccess called but no active 3DS session:",
                    result
                );
            };
            window.onError = (error: any) => {
                console.warn(
                    "[Global ZEUS SDK] onError called but no active 3DS session:",
                    error
                );
            };
            window._onError = (error: any) => {
                console.warn(
                    "[Global ZEUS SDK] _onError called but no active 3DS session:",
                    error
                );
            };
        };
    }, [sessionId, md, termUrl, iframeUrl, clientIp, onComplete, onError]);

    // React Portalを使ってdocument.bodyに直接レンダリング
    return ReactDOM.createPortal(
        <div className="three-ds-auth-container">
            <div className="three-ds-auth-modal">
                <div className="three-ds-auth-header">
                    <h2 className="three-ds-auth-title">カード認証</h2>
                </div>

                {isLoading && (
                    <div className="three-ds-auth-loading">
                        <div className="spinner"></div>
                        <p>認証画面を読み込んでいます...</p>
                    </div>
                )}

                {/* ZEUS SDKが動的にiframeを生成するためのコンテナ */}
                <div
                    id="3dscontainer"
                    ref={containerRef}
                    className="three-ds-auth-iframe"
                    style={{ display: isLoading ? "none" : "block" }}
                />
            </div>
        </div>,
        document.body
    );
};
